import React, { useState, useMemo, memo } from "react";
import {
  Backdrop,
  Rating,
  Box,
  Link as MuiLink,
  Typography,
  Chip,
  Collapse,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import moment from "moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import DurationFilter, {
  DURATION_OPTIONS,
  IDurationOption,
} from "../../../common/DurationFilter";
import PieChart, { IPieChartData } from "../../../common/PieChart";
import SortableTable, { TSortableColumn } from "../../../common/SortableTable";
import CircularProgress from "../../../../../components/CircularProgress";
import { Tooltip } from "../../../../../components";
import MetricPanel from "../../common/MetricPanel";

import { useAppSelector } from "../../../../../store/hooks";
import {
  capitalize,
  formatCurrency,
  formatNumber,
  formatPercent,
  isNumber,
} from "../../../../../helpers";
import { selectBuyBoxAnalysis } from "../../../../../features/productAnalyzer/productAnalyzer.selector";
import { TBuyBoxAnalysis } from "../../../../../features/productAnalyzer/productAnalyzer.types";
import { selectProductAnalyzerSettings } from "../../../../../features/settings/settings.selector";
// import { selectIsFreePlan } from "../../../../../features/pricingPlans/pricingPlans.selector";
// import LockOverlay from "../../../../../components/LockOverlay";
import { colors } from "../../config";
import useProductAnalyzerParams from "../../../../../hooks/useProductAnalyzerParams";

export type TSortColumn = {
  column: string;
  order: "asc" | "desc";
};

export type TBuyBoxAnalysisTableRow = TBuyBoxAnalysis & {
  sellerName: string;
  rating: number;
  ratingCount: number;
};

// Helper function to get price rank
const getPriceRank = (price: number, allPrices: number[]): string => {
  if (!price || !allPrices.length) return "-";
  const sortedPrices = [...allPrices].sort((a, b) => a - b);
  const rank = sortedPrices.indexOf(price) + 1;
  if (rank === 1) return "🥇 Lowest";
  if (rank === 2) return "2nd lowest";
  if (rank === 3) return "3rd lowest";
  return `${rank}th lowest`;
};

// Helper function to get stock indicator
const getStockIndicator = (stock: number) => {
  if (stock === 0)
    return { color: colors.error.text, label: "Out", dot: colors.error.text };
  if (stock < 10)
    return {
      color: colors.warning.text,
      label: "Low",
      dot: colors.warning.text,
    };
  if (stock < 50) return { color: "#ff9800", label: "Medium", dot: "#ff9800" };
  return {
    color: colors.success.text,
    label: "High",
    dot: colors.success.text,
  };
};

// Helper function to get competition level
const getCompetitionLevel = (
  sellerCount: number,
): { level: string; color: string } => {
  if (sellerCount <= 2) return { level: "Low", color: colors.success.text };
  if (sellerCount <= 5) return { level: "Medium", color: colors.warning.text };
  return { level: "High", color: colors.error.text };
};

// Helper function to get opportunity level
const getOpportunityLevel = (
  dominantWon: number,
  priceLeaderWon: number,
): { level: string; emoji: string; color: string } => {
  if (dominantWon > 90)
    return { level: "Low", emoji: "🔴", color: colors.error.text };
  if (dominantWon > 70)
    return { level: "Medium", emoji: "🟡", color: colors.warning.text };
  return { level: "High", emoji: "🟢", color: colors.success.text };
};

// Helper function to format date in compact relative format
const formatCompactRelativeTime = (date: string | Date): string => {
  if (!date) return "-";

  const now = moment();
  const then = moment(date);
  const diffSeconds = now.diff(then, "seconds");
  const diffMinutes = now.diff(then, "minutes");
  const diffHours = now.diff(then, "hours");
  const diffDays = now.diff(then, "days");
  const diffMonths = now.diff(then, "months");
  const diffYears = now.diff(then, "years");

  if (diffSeconds < 60) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
};

// Helper function to get recency info for visualization
const getRecencyInfo = (
  date: string | Date,
): {
  text: string;
  color: string;
  icon: React.ReactNode;
  tooltip: string;
} => {
  if (!date) {
    return {
      text: "-",
      color: colors.text.secondary,
      icon: null,
      tooltip: "No data available",
    };
  }

  const now = moment();
  const then = moment(date);
  const diffSeconds = now.diff(then, "seconds");
  const diffMinutes = now.diff(then, "minutes");
  const diffHours = now.diff(then, "hours");
  const diffDays = now.diff(then, "days");
  const diffMonths = now.diff(then, "months");
  const diffYears = now.diff(then, "years");

  const exactDate = moment(date).format("MMM DD, YYYY h:mm A");
  let text: string;
  let color: string;
  let icon: React.ReactNode;

  if (diffSeconds < 60) {
    text = "just now";
    color = colors.success.text;
    icon = (
      <CheckCircleIcon sx={{ fontSize: "12px", color: colors.success.text }} />
    );
  } else if (diffMinutes < 60) {
    text = `${diffMinutes}m ago`;
    color = colors.success.text;
    icon = (
      <CheckCircleIcon sx={{ fontSize: "12px", color: colors.success.text }} />
    );
  } else if (diffHours < 24) {
    text = `${diffHours}h ago`;
    color = colors.success.text;
    icon = (
      <AccessTimeIcon sx={{ fontSize: "12px", color: colors.success.text }} />
    );
  } else if (diffDays < 7) {
    text = `${diffDays}d ago`;
    color = colors.success.text;
    icon = (
      <AccessTimeIcon sx={{ fontSize: "12px", color: colors.success.text }} />
    );
  } else if (diffDays < 30) {
    text = `${diffDays}d ago`;
    color = colors.warning.text;
    icon = (
      <AccessTimeIcon sx={{ fontSize: "12px", color: colors.warning.text }} />
    );
  } else if (diffMonths < 12) {
    text = `${diffMonths}mo ago`;
    color = colors.warning.text;
    icon = (
      <AccessTimeIcon sx={{ fontSize: "12px", color: colors.warning.text }} />
    );
  } else {
    text = `${diffYears}y ago`;
    color = colors.error.text;
    icon = (
      <AccessTimeIcon sx={{ fontSize: "12px", color: colors.error.text }} />
    );
  }

  return {
    text,
    color,
    icon,
    tooltip: `Last won: ${exactDate}`,
  };
};

// Create table columns factory function to access all data for price ranking
const createTableColumns = (
  allData: TBuyBoxAnalysisTableRow[],
): TSortableColumn<TBuyBoxAnalysisTableRow>[] => {
  const allPrices = allData
    .filter((d) => d.avgPrice && d.avgPrice > 0)
    .map((d) => d.avgPrice);

  return [
    {
      header: "Seller",
      accessorKey: "sellerName",
      align: "left",
      html: (data: TBuyBoxAnalysisTableRow) => {
        const badgeStyles = {
          FBA: {
            bg: "#e8f5e9",
            color: "#2e7d32",
            label: "FBA",
          },
          FBM: {
            bg: "#fff3e0",
            color: "#ef6c00",
            label: "FBM",
          },
          AMZ: {
            bg: "#e3f2fd",
            color: "#1565c0",
            label: "AMZ",
          },
        };

        const badgeStyle =
          badgeStyles[data.type as keyof typeof badgeStyles] || badgeStyles.FBA;

        return (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
              {data.sellerId !== "-1" &&
                data.sellerId !== "-2" &&
                data.type !== "AMZ" && (
                  <MuiLink
                    component={Link}
                    to={data.link}
                    target="_blank"
                    sx={{
                      textDecoration: "none",
                      color: colors.text.primary,
                      fontSize: "13px",
                      fontWeight: 400,
                      "&:hover": {
                        textDecoration: "underline",
                        color: colors.primary.main,
                      },
                    }}
                  >
                    {data.sellerName || ""}
                  </MuiLink>
                )}
              {data.sellerId === "-1" && (
                <Typography fontSize="13px" color={colors.text.secondary}>
                  SUPPRESSED
                </Typography>
              )}
              {data.sellerId === "-2" && (
                <Typography fontSize="13px" color={colors.text.secondary}>
                  OUT OF STOCK
                </Typography>
              )}
              {data.type === "AMZ" && (
                <Typography
                  fontSize="13px"
                  fontWeight={500}
                  color={colors.text.primary}
                >
                  {data.sellerName}
                </Typography>
              )}
              {data.type && (
                <Chip
                  label={badgeStyle.label}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "9px",
                    fontWeight: 600,
                    backgroundColor: badgeStyle.bg,
                    color: badgeStyle.color,
                    border: "none",
                    "& .MuiChip-label": {
                      padding: "0 5px",
                    },
                  }}
                />
              )}
            </Box>
            {data.ratingCount > 0 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Rating
                  value={data.rating}
                  readOnly
                  precision={0.1}
                  sx={{ fontSize: "11px" }}
                />
                <Typography fontSize="10px" color={colors.text.secondary}>
                  (
                  {Intl.NumberFormat("en-US", { useGrouping: true }).format(
                    data.ratingCount,
                  )}
                  )
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
      wrap: true,
    },
    {
      header: "Avg Price",
      accessorKey: "avgPrice",
      html: (data: TBuyBoxAnalysisTableRow) => {
        const priceRank = getPriceRank(data.avgPrice || 0, allPrices);
        const isLowest = priceRank.includes("🥇");
        return (
          <Box display="flex" flexDirection="column" gap={0.25}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={0.5}
            >
              {isLowest && (
                <EmojiEventsIcon
                  sx={{ fontSize: "12px", color: colors.accent.orange }}
                />
              )}
              <Typography
                fontSize="14px"
                fontWeight={400}
                color={colors.text.primary}
                sx={{
                  fontSize: { xs: "12px", sm: "14px" },
                }}
              >
                {data.avgPrice ? formatCurrency(data.avgPrice) : "-"}
              </Typography>
            </Box>
            {priceRank !== "-" && (
              <Typography
                fontSize="9px"
                color={colors.text.secondary}
                sx={{
                  fontSize: { xs: "8px", sm: "9px" },
                }}
              >
                {priceRank}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      header: "Won",
      accessorKey: "won",
      formatter: (data: TBuyBoxAnalysisTableRow) =>
        isNumber(data.won) && data.won < 1
          ? "<1%"
          : formatPercent(Math.round(data.won), 0),
    },
    {
      header: "Stock",
      accessorKey: "stock",
      html: (data: TBuyBoxAnalysisTableRow) => {
        const stockInfo = getStockIndicator(data.stock || 0);
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            {data.stock !== 0 && (
              <InventoryIcon
                sx={{
                  fontSize: "14px",
                  color: stockInfo.dot,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              fontSize="12px"
              color={colors.text.primary}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.stock === 0
                ? "-"
                : data.stock < 50
                  ? formatNumber(data.stock, { useAbbreviation: false })
                  : "500+"}
            </Typography>
            {data.stock > 0 && data.stock < 10 && (
              <WarningIcon
                sx={{
                  fontSize: "14px",
                  color: colors.warning.text,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      header: "Last Won",
      accessorKey: "lastWon",
      wrap: true,
      html: (data: TBuyBoxAnalysisTableRow) => {
        const recencyInfo = getRecencyInfo(data.lastWon);

        return (
          <Tooltip title={recencyInfo.tooltip} placement="top">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={0.5}
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              {recencyInfo.icon && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {recencyInfo.icon}
                </Box>
              )}
              <Typography
                fontSize="12px"
                fontWeight={400}
                color={recencyInfo.color}
                sx={{
                  whiteSpace: "nowrap",
                }}
              >
                {recencyInfo.text}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      header: "Type",
      accessorKey: "type",
      hidden: true,
    },
  ];
};

const BuyBoxAnalysis: React.FC = () => {
  const theme = useTheme();
  const { defaultBbAnalysisTimeFrame } = useAppSelector(
    selectProductAnalyzerSettings,
  );
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const [duration, setDuration] = useState<string>(
    defaultBbAnalysisTimeFrame || DURATION_OPTIONS[1].id,
  );
  const [isChartCollapsed, setIsChartCollapsed] = useState(false);
  const { buyBoxAnalysis = [], loadingBuyBox } = useAppSelector((state) =>
    selectBuyBoxAnalysis(
      state,
      DURATION_OPTIONS.find((option) => option.id === duration)?.days ?? 0,
    ),
  );
  const { asin, lookupId } = useProductAnalyzerParams();

  const handleDurationChange = (option: IDurationOption) => {
    if (duration !== option.id) {
      setDuration(option.id);
    }
  };

  // Chart Data
  const chartsData = useMemo<IPieChartData[]>(() => {
    if (!buyBoxAnalysis || !buyBoxAnalysis.length) return [];

    return buyBoxAnalysis.map((item: TBuyBoxAnalysisTableRow) => ({
      name:
        item.sellerId === "-1"
          ? "SUPPRESSED"
          : item.sellerId === "-2"
            ? "OUT OF STOCK"
            : item.sellerName || "-",
      y: item.won,
      rating: item.rating ?? 0,
      ratingCount: item.ratingCount ?? 0,
      avgPrice: item.avgPrice ? formatCurrency(item.avgPrice) : "-",
      won: item.won < 1 ? "<1%" : Math.round(item.won) + "%",
      lastWon: item.lastWon ? capitalize(moment(item.lastWon).fromNow()) : "-",
      type: item.type,
      color: item.type === "AMZ" ? "#FF9900" : undefined,
    }));
  }, [buyBoxAnalysis]);

  // Insights calculations
  const insights = useMemo(() => {
    if (!buyBoxAnalysis || !buyBoxAnalysis.length) return null;

    const validSellers = buyBoxAnalysis.filter(
      (s) => s.sellerId !== "-1" && s.sellerId !== "-2",
    );
    const dominantSeller = [...buyBoxAnalysis].sort((a, b) => b.won - a.won)[0];
    const priceLeader = [...validSellers]
      .filter((s) => s.avgPrice && s.avgPrice > 0)
      .sort((a, b) => (a.avgPrice || 0) - (b.avgPrice || 0))[0];

    const competition = getCompetitionLevel(validSellers.length);
    const opportunity = getOpportunityLevel(
      dominantSeller.won,
      priceLeader?.won || 0,
    );

    return {
      dominantSeller: {
        name: dominantSeller.sellerName || "Unknown",
        won: dominantSeller.won,
      },
      priceLeader: {
        name: priceLeader?.sellerName || "Unknown",
        price: priceLeader?.avgPrice || 0,
      },
      competition: {
        level: competition.level,
        count: validSellers.length,
        color: competition.color,
      },
      opportunity: {
        level: opportunity.level,
        emoji: opportunity.emoji,
        color: opportunity.color,
      },
    };
  }, [buyBoxAnalysis]);

  // Table columns with access to all data
  const tableColumns = useMemo(
    () => createTableColumns(buyBoxAnalysis),
    [buyBoxAnalysis],
  );

  return (
    <>
      {/* Global styles for Portal-rendered components (Menu, etc.) */}
      <style>
        {`
          /* Override Menu (rendered in Portal) */
          .MuiMenu-paper {
            background-color: ${colors.background.white} !important;
            border: 1px solid ${colors.border.light} !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          }
          
          /* Override MenuItem */
          .MuiMenu-paper .MuiMenuItem-root {
            font-size: 12px !important;
            color: ${colors.text.primary} !important;
            padding: 8px 12px !important;
            min-height: 30px !important;
          }
          .MuiMenu-paper .MuiMenuItem-root:hover {
            background-color: ${colors.background.lightGray} !important;
          }
          
          /* Override Checkbox in Menu (custom styled checkbox with SVG background) */
          .MuiMenu-paper .MuiCheckbox-root {
            padding: 0 4px 0 0 !important;
          }
          /* Unchecked state - the span icon */
          .MuiMenu-paper .MuiCheckbox-root span {
            width: 18px !important;
            height: 18px !important;
            border-radius: 4px !important;
            border: 1px solid ${colors.border.light} !important;
            background-color: ${colors.background.white} !important;
            box-shadow: none !important;
          }
          /* Hover state for unchecked */
          .MuiMenu-paper .MuiCheckbox-root:hover span {
            background-color: ${colors.background.gray} !important;
          }
          /* Checked state - the span icon with checkmark */
          .MuiMenu-paper .MuiCheckbox-root.Mui-checked span {
            background-color: ${colors.primary.main} !important;
            border-color: ${colors.primary.main} !important;
          }
          /* Override the checkmark SVG background image with white checkmark */
          .MuiMenu-paper .MuiCheckbox-root.Mui-checked span::before {
            width: 16px !important;
            height: 16px !important;
            display: block !important;
            background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23FFFFFF'/%3E%3C/svg%3E") !important;
            content: '' !important;
          }
          /* Hover state for checked */
          .MuiMenu-paper .MuiCheckbox-root.Mui-checked:hover span {
            background-color: ${colors.primary.hover} !important;
          }
          
          /* Override Select in Menu */
          .MuiMenu-paper .MuiSelect-root {
            font-size: 12px !important;
            color: ${colors.text.primary} !important;
          }
          .MuiMenu-paper .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
            border-color: ${colors.border.light} !important;
          }
          .MuiMenu-paper .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${colors.primary.main} !important;
          }
          .MuiMenu-paper .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${colors.primary.main} !important;
            border-width: 1px !important;
          }
          
          /* Override Buttons in Menu */
          .MuiMenu-paper .MuiButton-root {
            font-size: 11px !important;
            font-weight: 500 !important;
            text-transform: none !important;
            min-width: 60px !important;
            padding: 4px 12px !important;
          }
          .MuiMenu-paper .MuiButton-contained {
            background-color: ${colors.primary.main} !important;
            color: ${colors.text.white} !important;
            border: 1px solid ${colors.primary.main} !important;
          }
          .MuiMenu-paper .MuiButton-contained:hover {
            background-color: ${colors.primary.hover} !important;
            border-color: ${colors.primary.hover} !important;
          }
          .MuiMenu-paper .MuiButton-outlined {
            background-color: ${colors.background.white} !important;
            color: ${colors.primary.main} !important;
            border: 1px solid ${colors.primary.main} !important;
          }
          .MuiMenu-paper .MuiButton-outlined:hover {
            background-color: ${colors.background.gray} !important;
            border-color: ${colors.primary.hover} !important;
          }
        `}
      </style>
      <Box
        sx={{
          // Override DurationFilter (ToggleButton from components)
          "& button[class*='ToggleButton']": {
            border: `1px solid ${colors.border.light} !important`,
            borderRadius: "4px !important",
            padding: "4px 12px !important",
            fontSize: "11px !important",
            fontWeight: "500 !important",
            color: `${colors.text.primary} !important`,
            backgroundColor: `${colors.background.white} !important`,
            textTransform: "none !important",
            minWidth: "40px !important",
            "&:hover": {
              backgroundColor: `${colors.background.gray} !important`,
              borderColor: `${colors.primary.main} !important`,
            },
            "&[aria-pressed='true']": {
              backgroundColor: `${colors.primary.main} !important`,
              color: `${colors.text.white} !important`,
              borderColor: `${colors.primary.main} !important`,
              "&:hover": {
                backgroundColor: `${colors.primary.hover} !important`,
                borderColor: `${colors.primary.hover} !important`,
              },
            },
          },

          // Override MUI ToggleButtonGroup if present
          "& .MuiToggleButtonGroup-root": {
            gap: "4px",
            "& .MuiToggleButton-root": {
              border: `1px solid ${colors.border.light} !important`,
              borderRadius: "4px !important",
              padding: "4px 12px !important",
              fontSize: "11px !important",
              fontWeight: "500 !important",
              color: `${colors.text.primary} !important`,
              backgroundColor: `${colors.background.white} !important`,
              textTransform: "none !important",
              "&:hover": {
                backgroundColor: `${colors.background.gray} !important`,
                borderColor: `${colors.primary.main} !important`,
              },
              "&.Mui-selected": {
                backgroundColor: `${colors.primary.main} !important`,
                color: `${colors.text.white} !important`,
                borderColor: `${colors.primary.main} !important`,
                "&:hover": {
                  backgroundColor: `${colors.primary.hover} !important`,
                  borderColor: `${colors.primary.hover} !important`,
                },
              },
            },
          },

          // Override IconButton in FilterButton and SortButton
          "& .MuiIconButton-root": {
            color: `${colors.primary.main} !important`,
            backgroundColor: `${colors.background.white} !important`,
            border: `1px solid ${colors.border.light} !important`,
            borderRadius: "4px !important",
            padding: "6px !important",
            "&:hover": {
              backgroundColor: `${colors.background.gray} !important`,
              borderColor: `${colors.primary.main} !important`,
            },
            "& .MuiSvgIcon-root": {
              fontSize: "18px !important",
              color: `${colors.primary.main} !important`,
            },
          },

          // Override SearchInput (MUI TextField)
          "& .MuiTextField-root": {
            "& .MuiOutlinedInput-root": {
              backgroundColor: `${colors.background.white} !important`,
              height: "32px !important",
              "& input": {
                color: `${colors.text.primary} !important`,
                fontSize: "12px !important",
                padding: "0 !important",
              },
              "& .MuiInputAdornment-root": {
                color: `${colors.text.secondary} !important`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: `${colors.primary.main} !important`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${colors.primary.main} !important`,
                borderWidth: "1px !important",
                boxShadow: `0 0 0 2px ${colors.primary.light} !important`,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${colors.border.light} !important`,
            },
          },

          // Note: Menu, Checkbox, Select, and Button overrides are in global <style> tag above
          // because they are rendered in Portals outside this component tree

          // Override PieChart (Highcharts) styles
          "& .highcharts-container": {
            fontFamily: "inherit !important",
          },
          "& .highcharts-root": {
            fontFamily: "inherit !important",
          },
          "& .highcharts-title": {
            fill: `${colors.text.primary} !important`,
            fontSize: "13px !important",
            fontWeight: "600 !important",
          },
          "& .highcharts-legend-item text": {
            fill: `${colors.text.primary} !important`,
            fontSize: "11px !important",
          },
          "& .highcharts-data-label text": {
            fill: `${colors.text.primary} !important`,
            fontSize: "11px !important",
          },
          // Highcharts tooltip - allow it to work naturally
          "& .highcharts-tooltip": {
            pointerEvents: "none !important",
            zIndex: "9999 !important",
          },
          "& .highcharts-tooltip-box": {
            fill: `${colors.text.primary} !important`,
            stroke: `${colors.border.light} !important`,
          },
          "& .highcharts-label": {
            color: `${colors.text.white} !important`,
          },
          "& .highcharts-tooltip text": {
            fill: `${colors.text.white} !important`,
            fontSize: "11px !important",
          },

          // Override SortableTable styles
          "& table": {
            fontSize: "12px !important",
            width: "100% !important",
            "& thead": {
              backgroundColor: `${colors.background.gray} !important`,
            },
            "& th, & .MuiTableCell-head": {
              backgroundColor: `${colors.background.gray} !important`,
              color: `${colors.text.primary} !important`,
              fontSize: "11px !important",
              fontWeight: "600 !important",
              padding: "6px !important",
              borderBottom: `1px solid ${colors.border.light} !important`,
              borderTop: `1px solid ${colors.border.light} !important`,
              borderRight: `1px solid ${colors.border.light} !important`,
              whiteSpace: "nowrap !important",
              height: "32px !important",
              "&:first-child": {
                borderLeft: `1px solid ${colors.border.light} !important`,
              },
              "@media (max-width: 600px)": {
                fontSize: "10px !important",
                padding: "4px !important",
              },
            },
            "& tbody tr": {
              backgroundColor: `${colors.background.white} !important`,
              "&:hover": {
                backgroundColor: `${colors.background.lightGray} !important`,
              },
            },
            "& td, & .MuiTableCell-body": {
              color: `${colors.text.primary} !important`,
              fontSize: "12px !important",
              fontWeight: "500 !important",
              padding: "6px !important",
              borderBottom: `1px solid ${colors.border.light} !important`,
              borderRight: `1px solid ${colors.border.light} !important`,
              height: "40px !important",
              "&:first-child": {
                borderLeft: `1px solid ${colors.border.light} !important`,
              },
              "@media (max-width: 600px)": {
                fontSize: "11px !important",
                padding: "4px !important",
              },
            },
            "& .MuiTableSortLabel-root": {
              color: `${colors.text.primary} !important`,
              fontSize: "11px !important",
              "&:hover": {
                color: `${colors.primary.main} !important`,
              },
              "&.Mui-active": {
                color: `${colors.primary.main} !important`,
                "& .MuiTableSortLabel-icon": {
                  color: `${colors.primary.main} !important`,
                },
              },
            },
            "& .MuiSvgIcon-root": {
              fontSize: "12px !important",
            },
          },

          // Override table container
          "& .MuiTableContainer-root": {
            backgroundColor: `${colors.background.white} !important`,
          },

          // Override Rating component
          "& .MuiRating-root": {
            color: `${colors.accent.orange} !important`,
            fontSize: "11px !important",
            "& .MuiRating-iconEmpty": {
              color: `${colors.border.light} !important`,
            },
            "& .MuiRating-iconFilled": {
              color: `${colors.accent.orange} !important`,
            },
          },

          // Override Typography (Last updated text, etc)
          "& .MuiTypography-root": {
            color: `${colors.text.secondary} !important`,
            "&.MuiTypography-body2": {
              fontSize: "12px !important",
            },
          },

          // Override MUI Tooltip (NOT Highcharts tooltips)
          "& .MuiTooltip-popper .MuiTooltip-tooltip": {
            backgroundColor: `${colors.text.primary} !important`,
            color: `${colors.text.white} !important`,
            fontSize: "11px !important",
          },
          "& .MuiTooltip-popper .MuiTooltip-arrow": {
            color: `${colors.text.primary} !important`,
          },

          // Override CircularProgress
          "& .MuiCircularProgress-root": {
            color: colors.primary.main,
          },

          // // Override LockOverlay
          // "& .MuiBackdrop-root": {
          //   backgroundColor: "rgba(255, 255, 255, 0.9)",
          // },
        }}
      >
        <Box
          sx={{
            mt: 1.5,
            mb: 1.25,
            borderRadius: "8px",
            background: colors.background.lightGray,
            px: 1,
            py: 0.75,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Duration Filter */}
          <Box mb={1}>
            <DurationFilter
              value={duration}
              onChange={handleDurationChange}
              withRange
            />
          </Box>

          {/* Insights Bar */}
          {insights && (
            <Box
              sx={{
                background: colors.background.white,
                py: 0.75,
                px: 0.75,
                mb: 1.375,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 0.75,
                  "@media (max-width: 360px)": {
                    gridTemplateColumns: "1fr",
                  },
                }}
              >
                <MetricPanel title="Dominant Seller" variant="default">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.25}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <EmojiEventsIcon
                        sx={{ fontSize: "14px", color: colors.accent.orange }}
                      />
                      <Typography
                        fontSize="12px"
                        fontWeight={500}
                        color={colors.text.primary}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: "120px", sm: "140px", md: "none" },
                        }}
                      >
                        {insights.dominantSeller.name}
                      </Typography>
                    </Box>
                    <Typography fontSize="11px" color={colors.text.secondary}>
                      {formatPercent(
                        Math.round(insights.dominantSeller.won),
                        0,
                      )}
                    </Typography>
                  </Box>
                </MetricPanel>
                <MetricPanel title="Price Leader" variant="default">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.25}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AttachMoneyIcon
                        sx={{ fontSize: "14px", color: colors.success.text }}
                      />
                      <Typography
                        fontSize="12px"
                        fontWeight={500}
                        color={colors.text.primary}
                      >
                        {formatCurrency(insights.priceLeader.price)}
                      </Typography>
                    </Box>
                    <Typography
                      fontSize="11px"
                      color={colors.text.secondary}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: { xs: "120px", sm: "140px", md: "none" },
                      }}
                    >
                      {insights.priceLeader.name}
                    </Typography>
                  </Box>
                </MetricPanel>
                <MetricPanel
                  title="Competition"
                  variant={
                    insights.competition.level === "Low"
                      ? "success"
                      : insights.competition.level === "Medium"
                        ? "warning"
                        : "fail"
                  }
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.25}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PeopleIcon
                        sx={{
                          fontSize: "14px",
                          color:
                            insights.competition.level === "Low"
                              ? colors.success.text
                              : insights.competition.level === "Medium"
                                ? colors.warning.text
                                : colors.error.text,
                        }}
                      />
                      <Typography fontSize="12px" fontWeight={500}>
                        {insights.competition.level}
                      </Typography>
                    </Box>
                    <Typography fontSize="11px" color={colors.text.secondary}>
                      {insights.competition.count} sellers
                    </Typography>
                  </Box>
                </MetricPanel>
                <MetricPanel
                  title="Opportunity"
                  variant={
                    insights.opportunity.level === "High"
                      ? "success"
                      : insights.opportunity.level === "Medium"
                        ? "warning"
                        : "fail"
                  }
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.25}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {insights.opportunity.level === "High" ? (
                        <TrendingUpIcon
                          sx={{ fontSize: "14px", color: colors.success.text }}
                        />
                      ) : insights.opportunity.level === "Medium" ? (
                        <WarningIcon
                          sx={{ fontSize: "14px", color: colors.warning.text }}
                        />
                      ) : (
                        <TrendingDownIcon
                          sx={{ fontSize: "14px", color: colors.error.text }}
                        />
                      )}
                      <Typography fontSize="12px" fontWeight={500}>
                        {insights.opportunity.level}
                      </Typography>
                    </Box>
                  </Box>
                </MetricPanel>
              </Box>
            </Box>
          )}

          {/* Chart Section with Collapse */}
          <Box
            sx={{
              background: colors.background.white,
              py: 0.75,
              px: 0.75,
              mb: "3px",
              borderRadius: "4px",
              border: `1px solid ${colors.border.dark}`,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                cursor: "pointer",
                py: 0.375,
                minHeight: "26px",
                "&:hover": {
                  backgroundColor: colors.primary.background,
                },
              }}
              onClick={() => setIsChartCollapsed(!isChartCollapsed)}
            >
              <Typography
                fontSize="12px"
                fontWeight={500}
                color={colors.text.primary}
                lineHeight="20px"
              >
                Buy Box Distribution
              </Typography>
              <SvgIcon
                component={KeyboardArrowDownIcon}
                sx={{
                  fontSize: "16px",
                  color: colors.primary.dark,
                  transition: "transform 0.25s ease",
                  transform: isChartCollapsed
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                }}
              />
            </Box>
            <Collapse
              in={!isChartCollapsed}
              sx={{
                "& .MuiCollapse-wrapperInner": {
                  pt: 0.25,
                  pb: 0.5,
                },
              }}
            >
              <Box
                width="100%"
                display="block"
                position="relative"
                sx={{
                  padding: { xs: "8px 0", sm: "16px 0" },
                }}
              >
                <PieChart data={chartsData} />
                {/* {isFreePlan && (
                  <LockOverlay
                    title="Buy Box Analysis"
                    asin={asin}
                    lookupId={lookupId}
                  />
                )} */}
              </Box>
            </Collapse>
          </Box>

          {/* Table */}
          <Box position="relative">
            <SortableTable
              columns={tableColumns}
              data={buyBoxAnalysis || []}
              defaultSort={{ accessorKey: "won", order: "desc" }}
              // lock={isFreePlan}
              title="Buy Box Analysis"
              // numberOfRowsToShow={isFreePlan ? 1 : 0}
            />

            {loadingBuyBox && (
              <Backdrop
                open={true}
                sx={{
                  position: "absolute",
                  zIndex: 999,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                }}
              >
                <CircularProgress size="md" />
              </Backdrop>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default memo(BuyBoxAnalysis);
